import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import JobCard from '../../components/JobCard';
import { Button } from '../../components/ui/button';
import { Loader2, ArrowRight, Search, Shield, Puzzle, HeartHandshake } from 'lucide-react';

// Config-driven "who you serve" landing pages. Backend already matches these
// population slugs (see routers/jobs.py POPULATIONS_SERVED).
export const POPULATION_PAGES = {
  veterans: {
    slug: 'veterans',
    icon: Shield,
    title: 'Social Work Jobs Serving Veterans & Military Families',
    metaTitle: 'Veterans & Military Social Work Jobs | VA, PTSD & Reintegration | INASW',
    metaDesc: 'Find social work, case management and behavioral health jobs serving Veterans, active-duty service members and military families — including VA, PTSD, reintegration and homeless-Veteran roles.',
    intro: 'Roles supporting Veterans and military families — VA medical centers, behavioral health, PTSD and trauma care, case management, and homeless-Veteran outreach. Organized by who you want to serve, not just your license.',
    facets: [
      { key: 'all', label: 'Current Opportunities', params: {} },
      { key: 'remote', label: 'Remote Opportunities', params: { is_remote: 'true' } },
      { key: 'va', label: 'VA & Medical', params: { search: 'Veterans Affairs' } },
      { key: 'ptsd', label: 'PTSD & Trauma', params: { search: 'PTSD' } },
      { key: 'behavioral', label: 'Behavioral Health', params: { search: 'behavioral health' } },
      { key: 'case-mgmt', label: 'Case Management', params: { search: 'case manager' } },
    ],
    aboutHeading: 'About Veterans & military social work roles',
    about: 'Social workers who serve Veterans go by many titles — VA Social Worker, Readjustment Counselor, Behavioral Health Clinician, Case Manager, and Homeless Veteran Outreach Coordinator. This page matches terms like Veteran, Military, Armed Forces, Service Member, Veterans Affairs, VA Medical, Active Duty, Post-9/11 and PTSD across every job in our nationwide database.',
    kicker: 'Special Populations & Skills',
  },
  'autism-idd': {
    slug: 'autism-idd',
    icon: Puzzle,
    title: 'Social Work Jobs in Autism, Intellectual & Developmental Disabilities',
    metaTitle: 'Autism & IDD Social Work Jobs | ABA, DD Waiver & Special Needs | INASW',
    metaDesc: 'Find social work and case management jobs serving people with autism and intellectual & developmental disabilities (IDD) — including ABA, DD Waiver, special needs and family support roles.',
    intro: 'Roles supporting people with autism and intellectual & developmental disabilities — DD Waiver case management, ABA-adjacent support, family services, and special-needs coordination. Organized by who you want to serve, not just your license.',
    facets: [
      { key: 'all', label: 'Current Opportunities', params: {} },
      { key: 'remote', label: 'Remote Opportunities', params: { is_remote: 'true' } },
      { key: 'waiver', label: 'DD Waiver / Case Mgmt', params: { search: 'waiver' } },
      { key: 'autism', label: 'Autism Services', params: { search: 'autism' } },
      { key: 'schools', label: 'Schools', params: { search: 'school' } },
      { key: 'family', label: 'Family Support', params: { search: 'family' } },
    ],
    aboutHeading: 'About Autism & IDD social work roles',
    about: 'Social workers in this field serve as DD Waiver Case Managers, Autism Services Coordinators, Special Needs Advocates, and Developmental Disabilities Specialists. This page matches terms like Autism, ASD, ABA, Intellectual Disability, Developmental Disability, IDD, I/DD, DD Waiver, Special Needs and Down Syndrome across every job in our nationwide database.',
    kicker: 'Special Populations & Skills',
  },
  'older-adults': {
    slug: 'older-adults',
    icon: HeartHandshake,
    title: 'Social Work Jobs Serving Older Adults & Aging',
    metaTitle: 'Older Adults & Aging Social Work Jobs | Geriatric, Hospice & Memory Care | INASW',
    metaDesc: 'Find geriatric social work, hospice, memory care and aging-services jobs serving older adults — including medical social work, care coordination, dementia and Alzheimer support roles.',
    intro: 'Roles supporting older adults — geriatric social work, hospice and palliative care, memory care, care coordination, and aging-in-place services. Organized by who you want to serve, not just your license.',
    facets: [
      { key: 'all', label: 'Current Opportunities', params: {} },
      { key: 'remote', label: 'Remote Opportunities', params: { is_remote: 'true' } },
      { key: 'hospice', label: 'Hospice & Palliative', params: { search: 'hospice' } },
      { key: 'memory', label: 'Memory Care', params: { search: 'dementia' } },
      { key: 'medical', label: 'Medical Social Work', params: { search: 'medical social worker' } },
      { key: 'care-coord', label: 'Care Coordination', params: { search: 'care coordination' } },
    ],
    aboutHeading: 'About Older Adults & aging social work roles',
    about: 'Social workers who serve older adults go by titles like Geriatric Social Worker, Hospice Social Worker, Medical Social Worker, Care Coordinator, and Memory Care Specialist. This page matches terms like Older Adult, Geriatric, Gerontology, Elderly, Senior, Aging, Hospice, Memory Care, Dementia and Alzheimer across every job in our nationwide database.',
    kicker: 'Special Populations & Skills',
  },
};

const PopulationLandingPage = ({ populationKey }) => {
  const cfg = POPULATION_PAGES[populationKey];
  const [facet, setFacet] = useState('all');
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const Icon = cfg.icon;

  useEffect(() => {
    document.title = cfg.metaTitle;
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) { tag = document.createElement('meta'); tag.setAttribute('name', 'description'); document.head.appendChild(tag); }
    const prev = tag.getAttribute('content');
    tag.setAttribute('content', cfg.metaDesc);
    return () => { if (prev) tag.setAttribute('content', prev); };
  }, [cfg]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const active = cfg.facets.find((f) => f.key === facet) || cfg.facets[0];
      const params = new URLSearchParams({ populations: cfg.slug, limit: '50', ...active.params });
      const r = await axios.get(`${API_URL}/jobs?${params.toString()}`);
      setJobs(r.data.jobs || []);
      setTotal(r.data.total || 0);
    } catch (e) { console.error('Population jobs load failed:', e); } finally { setLoading(false); }
  }, [facet, cfg]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-gray-50 pt-20" data-testid={`population-page-${cfg.slug}`}>
      <section className="bg-primary text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-16">
          <div className="flex items-center gap-2 text-secondary font-semibold text-sm mb-3">
            <Icon className="w-5 h-5" /> {cfg.kicker}
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold leading-tight max-w-3xl">{cfg.title}</h1>
          <p className="mt-4 text-white/85 text-base md:text-lg max-w-2xl">{cfg.intro}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to={`/jobs?populations=${cfg.slug}`} data-testid={`see-all-${cfg.slug}`}>
              <Button className="bg-secondary hover:bg-secondary-hover text-white h-11">
                Browse in full search <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap gap-2 mb-6" data-testid={`facets-${cfg.slug}`}>
          {cfg.facets.map((f) => (
            <button
              key={f.key}
              onClick={() => setFacet(f.key)}
              data-testid={`facet-${f.key}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${facet === f.key ? 'bg-primary text-white' : 'bg-white text-gray-700 border border-gray-200 hover:border-primary'}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <p className="text-sm text-gray-500 mb-4" data-testid={`jobs-count-${cfg.slug}`}>
          {loading ? 'Loading…' : `${total.toLocaleString()} ${total === 1 ? 'job' : 'jobs'} found`}
        </p>

        {loading ? (
          <div className="py-16 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : jobs.length === 0 ? (
          <div className="bg-white border border-gray-100 rounded-2xl p-10 text-center" data-testid={`jobs-empty-${cfg.slug}`}>
            <Search className="w-8 h-8 text-gray-300 mx-auto mb-3" />
            <h3 className="font-serif text-xl font-bold text-primary mb-2">No matching roles right now</h3>
            <p className="text-gray-500 max-w-md mx-auto text-sm">
              New roles are added as employers post and as we import them nationwide. Save this search or set a job alert so you're the first to know.
            </p>
            <Link to={`/jobs?populations=${cfg.slug}`} className="mt-4 inline-block">
              <Button variant="outline" className="h-10">Set up a job alert</Button>
            </Link>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {jobs.map((job) => (<JobCard key={job.job_id} job={job} />))}
          </div>
        )}

        <div className="mt-12 bg-white border border-gray-100 rounded-2xl p-6 md:p-8 prose prose-sm max-w-none">
          <h2 className="font-serif text-2xl font-bold text-primary">{cfg.aboutHeading}</h2>
          <p className="text-gray-600">{cfg.about}</p>
        </div>
      </div>
    </div>
  );
};

export default PopulationLandingPage;
